import {
    ActivityCategory,
    outcomeGoalMetricLabels,
    outcomeGoalMetrics,
    RegionId,
    unknownActivityTypeLabel,
    zeroTargets,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { PlanHealthBanner } from "components/marketing-plan/plan-health-banner";
import { PlanModal } from "components/marketing-plan/plan-modal";
import { usePlanHealth } from "components/marketing-plan/use-plan-health";
import { trpc } from "components/rpc/trpc";
import {
    ActivityTypeOption,
    useActivityTypeOptionsState,
} from "components/tasks/activity-type-options";
import { Spinner } from "components/ui/spinner";
import React, { useState } from "react";
import { useFeature, useNow, useRegionId } from "store/selectors/hooks";
import { compareGoalColumnOrder } from "./goal-columns";
import { MetricCard } from "./metric-card";
import { MetricProgressTable } from "./metric-progress-table";
import {
    activityCategoryBarColor,
    currentYearMonth,
    monthElapsedFraction,
    outcomeGoalMetricBarColor,
    ymAddMonths,
} from "./pace";
import { RecentActivityList } from "./recent-activity-list";
import { trackedMetrics as metricProgressMetrics } from "./use-metric-progress";

interface GoalCardEntry {
    key: string;
    label: string;
    barColor: string;
    actual: number;
    target: number;
    prior: number;
}

export const MyGoalsContent: React.FC = () => {
    const now = useNow();
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");
    const { options: activityTypeOptions, isLoading: optionsLoading } =
        useActivityTypeOptionsState();
    const month = currentYearMonth(now);
    const priorMonth = ymAddMonths(month, -1);
    const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
    const planHealth = usePlanHealth();

    const currentQuery = useQuery({
        ...trpcClient.marketingGoal.myAttainment.queryOptions({ month }),
    });
    const priorQuery = useQuery({
        ...trpcClient.marketingGoal.myAttainment.queryOptions({ month: priorMonth }),
    });
    const recentQuery = useQuery({
        ...trpcClient.marketingGoal.myRecentActivity.queryOptions({
            regionId: regionId ?? ("" as RegionId),
            month,
        }),
        enabled: !!regionId,
    });

    const attainment = currentQuery.data;
    const frac = monthElapsedFraction(month, now);
    const goal = attainment?.goal ?? null;
    const actuals = attainment?.actuals ?? zeroTargets();
    const priorActuals = priorQuery.data?.actuals ?? zeroTargets();
    const optionsById = new Map<string, ActivityTypeOption>(
        activityTypeOptions.map(option => [option.id, option]),
    );

    const entryCategory = (taskTypeId: string): ActivityCategory =>
        optionsById.get(taskTypeId)?.category ?? "other";

    const activityEntries: GoalCardEntry[] = goal
        ? Object.entries(goal.activity)
              .filter(([, target]) => target > 0)
              .map(([taskTypeId, target]) => {
                  const option = optionsById.get(taskTypeId);
                  return {
                      key: taskTypeId,
                      label: option?.name ?? unknownActivityTypeLabel,
                      barColor: activityCategoryBarColor[option?.category ?? "other"],
                      actual: actuals.activity[taskTypeId] ?? 0,
                      target,
                      prior: priorActuals.activity[taskTypeId] ?? 0,
                  };
              })
              .sort((a, b) =>
                  compareGoalColumnOrder(
                      { category: entryCategory(a.key), label: a.label },
                      { category: entryCategory(b.key), label: b.label },
                  ),
              )
        : [];
    const outcomeEntries: GoalCardEntry[] = goal
        ? outcomeGoalMetrics
              .filter(metric => goal[metric] > 0)
              .map(metric => ({
                  key: metric,
                  label: outcomeGoalMetricLabels[metric],
                  barColor: outcomeGoalMetricBarColor[metric],
                  actual: actuals[metric],
                  target: goal[metric],
                  prior: priorActuals[metric],
              }))
        : [];
    const entries = [...activityEntries, ...outcomeEntries];

    return (
        <div className="space-y-4">
            {currentQuery.isLoading || optionsLoading ? (
                <div className="flex justify-center rounded-lg bg-white p-12 ring-1 ring-gray-200">
                    <Spinner />
                </div>
            ) : currentQuery.isError ? (
                <div className="rounded-xl bg-white p-8 text-center text-sm text-red-600 ring-1 ring-gray-200">
                    We couldn&apos;t load your goals. Please try again.
                </div>
            ) : entries.length === 0 ? (
                marketingPlaybookEnabled ? (
                    <PlanHealthBanner
                        planHealth={planHealth}
                        onViewPlan={() => setIsPlanModalOpen(true)}
                        noPlanMessage="You haven't set up any touchpoints in your marketing playbook yet."
                    />
                ) : (
                    <div className="rounded-xl bg-white p-8 text-center text-sm text-gray-600 ring-1 ring-gray-200">
                        No marketing goals have been set for you yet.
                    </div>
                )
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {entries.map(entry => (
                            <MetricCard
                                key={entry.key}
                                label={entry.label}
                                barColor={entry.barColor}
                                actual={entry.actual}
                                target={entry.target}
                                frac={frac}
                                footer={
                                    <>
                                        Prior month:{" "}
                                        <span className="font-semibold tabular-nums text-gray-700">
                                            {entry.prior}
                                        </span>
                                    </>
                                }
                            />
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="h-6 w-1 flex-shrink-0 rounded-full bg-brand-500" />
                        <h2 className="twoverride text-lg font-semibold text-gray-800">
                            Activity Progress
                        </h2>
                    </div>
                    <PlanHealthBanner
                        planHealth={planHealth}
                        onViewPlan={() => setIsPlanModalOpen(true)}
                        noPlanMessage="You haven't set up any touchpoints in your marketing playbook yet."
                    />
                    <div className="space-y-3 rounded-xl bg-white px-3 py-4 ring-1 ring-gray-200 sm:px-4">
                        <MetricProgressTable
                            metrics={metricProgressMetrics}
                            density="comfortable"
                        />
                    </div>
                    <RecentActivityList recent={recentQuery.data ?? []} />
                </>
            )}
            <PlanModal
                isOpen={isPlanModalOpen}
                onDismiss={() => setIsPlanModalOpen(false)}
            />
        </div>
    );
};
